import { useEffect, useState } from 'react';
import { Spin } from 'antd';
import CommentForm from './CommentForm';
import styles from './index.module.scss';
import SortBy from './SortBy';
import {
  CommentListParams,
  CommentOrderColumn,
} from '../../../store/comment/list';
import { useCommentList } from '../../Pages/Categories/[categoryId]/useCommentList';
import { Comment } from '../../../store/comment';
import CommentsList from './CommentsList';
import { PaginationProps } from '../../../store/types';

interface CommentsProps {
  filter: Pick<
    CommentListParams,
    | 'article'
    | 'category'
    | 'subCategory'
    | 'entity'
    | 'postId'
    | 'international'
  >;
  title?: React.ReactNode;
  ownerID?: string | undefined;
}

function Comments({ filter, title, ownerID }: CommentsProps) {
  const { getComments, list } = useCommentList();

  const [listData, setListData] = useState<Comment[]>([]);
  const [pagination, setPagination] = useState<
    PaginationProps<CommentOrderColumn>
  >({
    page: 1,
    pageSize: 10,
    orderBy: 'DESC',
    orderByColumn: 'createdAt',
  });

  const [meta, setMeta] = useState({
    itemCount: 0,
    page: 1,
    pageSize: 10,
    hasNextPage: false,
  });

  useEffect(() => {
    const params = {
      ...filter,
      status: 'published' as const,
      ...pagination,
    };

    getComments(params);
  }, [filter, pagination]);

  useEffect(() => {
    if (list.response?.data) {
      const isFirstPage = list.response.meta.page === 1;
      const newData = list.response.data;

      if (isFirstPage) {
        setListData(newData);
      } else {
        setListData((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNew = newData.filter((item) => !existingIds.has(item.id));

          return [...prev, ...uniqueNew];
        });
      }

      setMeta(list.response.meta);
    }
  }, [list.response?.data]);

  const onSubmitNewComment = () => {
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        {title ?? (
          <h4 className={styles.title}>
            Share Your Thoughts and Join the Conversation!
          </h4>
        )}

        <div className={styles.controlls}>
          <div className={styles.count}>
            {`${meta.itemCount || 0} ${
              `Comment${(meta.itemCount || 0) !== 1 ? 's' : ''}`
            }`}
          </div>

          {(meta.itemCount || 0) > 1 && (
            <div className={styles.sort}>
              <SortBy
                onChange={(params) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: 1,
                    ...params,
                  }));
                }}
              />
            </div>
          )}
        </div>
      </div>

      <CommentForm
        createParams={{
          body: {
            ...(filter.category && { categoryId: filter.category }),
            ...(filter.subCategory && { subCategoryId: filter.subCategory }),
            ...(filter.article && { articleId: filter.article }),
            ...(filter.postId && { postId: filter.postId }),
            ...(filter.international && {
              internationalId: filter.international,
            }),
            entity: filter.postId ? 'post' : filter.entity, // Kill me here
          },
        }}
        onSuccess={onSubmitNewComment}
      />

      <Spin spinning={list.loading}>
        <CommentsList
          ownerId={ownerID}
          data={listData}
          setData={setListData}
          onLoadMore={() => {
            setPagination((prev) => ({
              ...prev,
              page: prev.page + 1,
            }));
          }}
          loading={list.loading}
          hasNextPage={!!list.response?.meta.hasNextPage}
          onRepplyDeleted={(parentId: string) => {
            setListData((prev) => prev.map((item) => (item.id === parentId
              ? {
                ...item,
                numberOfCommentsTotal: item.numberOfCommentsTotal - 1,
              }
              : item)));
          }}
          onCommentDeleted={() => {
            setMeta((prev) => ({
              ...prev,
              itemCount: prev.itemCount - 1,
            }));
          }}
        />
      </Spin>
    </div>
  );
}

export default Comments;
